import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Chip, Alert, CircularProgress,
  List, ListItem, ListItemText, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';

// Context
import { useStock } from '../contexts/StockContext';
import {
  Warning as WarningIcon,
  Send as SendIcon,
  LocalShipping as LocalShippingIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { dashboardService } from '../services/dashboardService';
import { supplierService } from '../services/supplierService';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useFormatageDates } from '../utils/dates';
import { formatCurrency } from '../utils/currency';
import { quantiteACombler } from '../utils/reappro';
import { grouperParJour } from '../utils/graphiqueMouvements';
import BoutonToutCommander from '../components/tableau-de-bord/BoutonToutCommander';
import EnteteEcran from '../components/commun/EnteteEcran';

// Les couleurs des cinq types d'activité — des JETONS, jamais des littéraux.
const COULEUR_ACTIVITE = {
  entree: 'success',
  sortie: 'error',
  reception: 'info',
  paiement: 'primary',
  envoi: 'default',
};

const Dashboard = ({ showSnackbar }) => {
  const { refreshTrigger, currentStock } = useStock();
  const { t } = useLanguage();
  // ⚠️ `…EnJour`, PAS `formaterDateSaisie` : ces trois-là sont des `created_at`,
  // donc des INSTANTS, dont on ne montre que le jour. Ils doivent être
  // convertis dans le fuseau du lecteur avant qu'on en garde la date.
  const { formaterHorodatage, formaterHorodatageEnJour, formaterJourCourt } =
    useFormatageDates();
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ⚠️ LES FOURNISSEURS SERVENT UNIQUEMENT À NOMMER LES PANIERS de « Tout
  // commander ». Ils sont chargés à part, et leur échec n'empêche RIEN : le
  // dialogue tomberait sur `#3` au lieu du nom. *Une information de confort ne
  // doit pas pouvoir faire échouer un écran.*
  const [fournisseurs, setFournisseurs] = useState([]);
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);

  // Fonction pour traduire le titre d'une notification
  const translateNotificationTitle = (notification) => {
    const { type, product_name } = notification;
    
    switch (type) {
      case 'low_stock':
        return t('notifications.titleLowStock', { productName: product_name || '' });
      case 'out_of_stock':
        return t('notifications.titleOutOfStock', { productName: product_name || '' });
      case 'order_received':
        return t('notifications.titleOrderReceived', { productName: product_name || '' });
      default:
        // Si on ne peut pas traduire, utiliser le titre original
        return notification.title;
    }
  };

  // 🔴 `translateNotificationMessage` A ÉTÉ RETIRÉE AU LOT RU6. Le nouveau
  // bloc de notifications n'affiche que le TITRE et l'horodatage : le message
  // long tenait dans une carte à lui, il ne tient pas dans une ligne de liste.
  // ⚠️ La fonction survivait en double — `Notifications.js:170` en porte une
  // copie, qui reste la seule. *Une fonction sans appelant n'est pas une
  // réserve : c'est une divergence en attente avec sa jumelle.*

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getDashboard();
      setDashboardData(data);
      // Chargé APRÈS et à part : son échec ne doit pas emporter le tableau.
      try {
        const f = await supplierService.getSuppliers({ per_page: 100 });
        setFournisseurs(f.suppliers || []);
      } catch (e) {
        setFournisseurs([]);
      }
    } catch (err) {
      // 🔴 ON STOCKE LA CLÉ, ON TRADUIT AU RENDU. `setError(t(…))` fige la
      // traduction à l'instant de l'erreur, et `translations` démarre à `{}`,
      // rempli par un `fetch` : un serveur arrêté fait échouer l'appel API
      // AVANT le dictionnaire, et la clé brute reste gelée dans l'état, lisible
      // par l'utilisateur. C'est le défaut D2 de RU5-e, et cet écran était l'un
      // des 13 sites restants. *Une chaîne traduite rangée dans un état est une
      // traduction datée.*
      setError('dashboard.loadError');
      showSnackbar(t('dashboard.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showSnackbar]);

  // Charger les données uniquement au montage initial
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      // Délai pour éviter les conflits ResizeObserver lors de l'initialisation
      const timer = setTimeout(() => {
        loadDashboardData();
      }, 200);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides - on charge uniquement au montage

  // Effet pour recharger les données quand le stock change
  useEffect(() => {
    // Si on a déjà chargé une fois et que le stock change, recharger immédiatement
    if (hasLoadedRef.current && currentStock?.id) {
      loadDashboardData();
    }
  }, [currentStock?.id, loadDashboardData]);

  // Effet pour recharger les données quand refreshTrigger change
  useEffect(() => {
    if (refreshTrigger > 0 && hasLoadedRef.current) {
      loadDashboardData();
    }
  }, [refreshTrigger, loadDashboardData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {/* La traduction se fait ICI, au rendu — l'état ne porte qu'une clé. */}
        {t(error)}
      </Alert>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const {
    indicateurs, low_stock_products, mouvements_periode, jours_du_graphique,
    activite_recente, recent_notifications,
  } = dashboardData;

  // ── LES QUATRE VERBES DU CYCLE D'ACHAT ────────────────────────────────────
  // 🔴 QUATRE ACTIONS, PAS QUATRE COMPTEURS, et elles se suivent :
  // à réapprovisionner → à envoyer → à recevoir → à payer. Chacune MÈNE à son
  // écran : *une carte qui affiche un compte sans y conduire promet un geste
  // qu'elle ne rend pas.*
  // ⚠️ SEULE LA PREMIÈRE SE TEINTE, et seulement s'il y a des RUPTURES. Les
  // ruptures ne sont pas une cinquième carte : un même chiffre à deux endroits
  // finit par en dire deux différents — c'est le défaut D1 de RU5-e.
  const ruptures = indicateurs?.ruptures ?? 0;
  const cartes = [
    {
      cle: 'reappro',
      libelle: t('dashboard.toRestock'),
      valeur: indicateurs?.a_reapprovisionner ?? 0,
      icone: <WarningIcon sx={{ fontSize: 40 }} color={ruptures > 0 ? 'error' : 'warning'} />,
      vers: '/products',
      etat: { lowStockOnly: true },
      couleur: ruptures > 0 ? 'error.main' : 'warning.main',
      teinte: ruptures > 0,
      detail: ruptures > 0 ? t('dashboard.outOfStockCount', { count: ruptures }) : null,
    },
    {
      cle: 'envoyer',
      libelle: t('dashboard.toSend'),
      valeur: indicateurs?.a_envoyer ?? 0,
      icone: <SendIcon sx={{ fontSize: 40 }} color="info" />,
      vers: '/purchase-orders',
      couleur: 'text.primary',
    },
    {
      cle: 'recevoir',
      libelle: t('dashboard.toReceive'),
      valeur: indicateurs?.a_recevoir ?? 0,
      icone: <LocalShippingIcon sx={{ fontSize: 40 }} color="info" />,
      vers: '/order-reception',
      couleur: 'text.primary',
    },
    {
      cle: 'payer',
      libelle: t('dashboard.toPay'),
      valeur: indicateurs?.a_payer ?? 0,
      icone: <PaymentsIcon sx={{ fontSize: 40 }} color="info" />,
      vers: '/accounting',
      couleur: 'text.primary',
    },
  ];

  // ⚠️ LE REGROUPEMENT SE FAIT DANS LE FUSEAU DU LECTEUR, et la conversion
  // vient de `utils/dates.js` — elle n'est pas réécrite ici.
  const graphique = grouperParJour(
    mouvements_periode, jours_du_graphique || 7, new Date(),
    formaterHorodatageEnJour, formaterJourCourt,
  );

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      {/* 🔴 LE DÉGRADÉ A QUITTÉ CE TITRE (lot RU2), ET LA DISTINCTION MÉRITE
          D'ÊTRE ÉCRITE pour que personne « n'harmonise » les trois derniers
          emplois du motif dans un sens ou dans l'autre :

            · `Sidebar` (le logo) et `Login` (l'écran de connexion) le GARDENT
              — ce sont des surfaces de MARQUE ;
            · le tableau de bord le PERD — c'est un écran de TRAVAIL, comme
              Produits, Commandes, Fournisseurs, Réception et Comptabilité.

          Un titre violet et énorme quand ses cinq voisins sont sobres, c'est
          un écran de plus qui crie. Il porte désormais la même entête que les
          autres, sous-titre compris. */}
      <EnteteEcran titre={t('dashboard.title')} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 3 }}>
        {t('dashboard.subtitle')}
      </Typography>

      {/* ── LES QUATRE VERBES DU CYCLE D'ACHAT ─────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cartes.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.cle}>
            <Card
              onClick={() => navigate(c.vers, c.etat ? { state: c.etat } : undefined)}
              sx={{
                cursor: 'pointer', height: '100%',
                // ⚠️ `alpha()` SUR UN JETON, jamais une couleur figée — même
                // mécanique que la ligne teintée de RU3-a et que la carte
                // d'en-cours de RU5-e.
                ...(c.teinte ? {
                  backgroundColor: (theme) => alpha(theme.palette.error.main,
                                                    theme.palette.mode === 'dark' ? 0.13 : 0.13),
                } : {}),
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography color="text.secondary" gutterBottom>
                      {c.libelle}
                    </Typography>
                    {/* La couleur du texte est DÉCLARÉE même quand la carte ne
                        se teinte pas — garde Q du lot RU4-c′ : toute surface
                        qui porte un fond de statut déclare sa couleur. */}
                    <Typography variant="h4" sx={{ color: c.couleur }}>
                      {c.valeur}
                    </Typography>
                    {c.detail && (
                      <Typography variant="caption" sx={{ color: 'error.main' }}>
                        {c.detail}
                      </Typography>
                    )}
                  </Box>
                  {c.icone}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* ── LES PRODUITS SOUS LE SEUIL, ET « TOUT COMMANDER » ─────────── */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,
                         flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {t('dashboard.lowStockProducts')}
                </Typography>
                <BoutonToutCommander
                  produits={low_stock_products}
                  stockId={currentStock?.id}
                  fournisseurs={fournisseurs}
                  onFait={({ paniers, lignes }) => {
                    showSnackbar(t('dashboard.orderAllDone', { carts: paniers, lines: lignes }),
                                 'success');
                    navigate('/purchase-orders');
                  }}
                />
              </Box>

              {(!low_stock_products || low_stock_products.length === 0) ? (
                <Typography color="text.secondary">
                  {t('dashboard.noLowStockProducts')}
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('products.name')}</TableCell>
                        <TableCell align="right">{t('products.currentStock')}</TableCell>
                        <TableCell align="right">{t('common.minStock')}</TableCell>
                        {/* 🔴 LA QUANTITÉ À COMBLER EST AFFICHÉE, pas seulement
                            le stock. C'est le chiffre que « Tout commander »
                            va employer : le montrer permet de le contredire
                            AVANT le geste, pas après. */}
                        <TableCell align="right">{t('dashboard.toOrder')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {low_stock_products.map((p) => (
                        <TableRow key={p.id} hover sx={{ cursor: 'pointer' }}
                                  onClick={() => navigate(`/products/${p.id}`)}>
                          <TableCell>
                            <Typography variant="body2">{p.name}</Typography>
                            {!p.supplier_id && (
                              <Typography variant="caption" color="error.main" display="block">
                                {t('dashboard.noSupplier')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right"
                                     sx={{ color: p.current_stock === 0
                                       ? 'error.main' : 'warning.main' }}>
                            {p.current_stock}
                          </TableCell>
                          <TableCell align="right">{p.min_stock}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {quantiteACombler(p)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── LE GRAPHIQUE DE LA SEMAINE ────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.weeklyMovements')}
              </Typography>
              {/* 🔴 LES COULEURS VIENNENT DU THÈME, PAS DE LITTÉRAUX — c'est
                  pour cela que `@mui/x-charts` a été retenu contre `recharts` :
                  il consomme la palette MUI, donc il suit les deux modes sans
                  qu'on écrive une seule couleur. */}
              {/* 🔴 LE GRAPHIQUE RESTE EN GAUCHE-DROITE — PAR UN ATTRIBUT,
                  ET C'EST LA TROUVAILLE DU LOT.

                  La première version écrivait `sx={{ direction: 'ltr' }}` sur
                  le `<BarChart>`. **Elle n'a JAMAIS été appliquée** :
                  `stylis-plugin-rtl` retourne `direction` ELLE-MÊME —
                  `direction:ltr` en source, **`direction:rtl` émis**. *Le
                  correctif posé pour échapper au retournement a été retourné
                  par le plugin qu'il devait contourner.*
                  ⚠️ Et la garde était VERTE : elle lisait la SOURCE, pas le CSS
                  émis. *Une garde qui lit la source ne voit pas un plugin qui
                  la réécrit.*

                  🔴 L'ATTRIBUT `dir` N'EST PAS DU CSS, donc aucun plugin de
                  feuille de style ne peut l'atteindre. Mesuré avec son témoin :
                    · `sx` retourné + `dir="ltr"` sur l'enveloppe → **rtl**, le
                      `sx` gagne (une règle de classe bat le `dir` hérité) ;
                    · **AUCUN `sx`** + `dir="ltr"` sur l'enveloppe → **ltr** ✅ ;
                    · témoin sans rien → **rtl**, l'héritage fonctionne.
                  D'où les deux moitiés du correctif : **retirer le `sx`** ET
                  poser l'attribut. L'une sans l'autre ne suffit pas.

                  ⚠️ CE QUE ÇA CORRIGE, MESURÉ SUR LA PAGE : la légende. Le
                  libellé `כניסות` occupait 225→270 et le carré de couleur
                  245→265 — **le carré était entièrement DANS le texte**, parce
                  que `ChartsLegend` code en dur `textAnchor: 'start'`
                  (`ChartsLegend.js:82`), et qu'en RTL « start » veut dire
                  DROITE. Direction réellement appliquée : le libellé passe à
                  270→315, hors du carré.

                  🔴 ET LA GÉOMÉTRIE, ELLE, N'A JAMAIS BOUGÉ : barres à x=20 et
                  x=120 dans les deux sens, étiquettes d'axe en
                  `text-anchor: middle` inchangées. **Le SVG a son propre
                  système de coordonnées.** *Un axe temporel qui va à rebours
                  serait illisible pour tout le monde, y compris pour celui qui
                  lit de droite à gauche.* */}
              <Box dir="ltr">
              <BarChart
                height={280}
                xAxis={[{ scaleType: 'band', data: graphique.etiquettes }]}
                series={[
                  { data: graphique.entrees, label: t('dashboard.entries'),
                    color: theme.palette.success.main },
                  { data: graphique.sorties, label: t('dashboard.exits'),
                    color: theme.palette.error.main },
                ]}
                margin={{ top: 30, right: 10, bottom: 30, left: 40 }}
              />
              </Box>
              {/* ⚠️ CE QUI N'EST PAS DANS L'AXE EST DIT, PAS PERDU. Le serveur
                  borne en INSTANTS, l'axe compte des JOURS locaux : aux deux
                  extrémités les découpes ne coïncident pas exactement. */}
              {graphique.horsAxe > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.movementsOutsideAxis', { count: graphique.horsAxe })}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── L'ACTIVITÉ RÉCENTE, TOUTES SOURCES CONFONDUES ─────────────── */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.recentActivity')}
              </Typography>
              {(!activite_recente || activite_recente.length === 0) ? (
                <Typography color="text.secondary">
                  {t('dashboard.noRecentActivity')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {activite_recente.map((e, i) => (
                    <ListItem key={`${e.type}-${e.horodatage}-${i}`} disableGutters>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                     flexWrap: 'wrap' }}>
                            <Chip size="small" variant="outlined"
                                  color={COULEUR_ACTIVITE[e.type] || 'default'}
                                  label={t(`dashboard.activity_${e.type}`)} />
                            <Typography variant="body2">{e.libelle || '—'}</Typography>
                            {e.quantite != null && (
                              <Typography variant="body2"
                                          sx={{ color: e.type === 'entree'
                                            ? 'success.main' : 'error.main' }}>
                                {e.type === 'entree' ? '+' : '−'}{e.quantite}
                              </Typography>
                            )}
                            {e.montant != null && (
                              <Typography variant="body2">
                                {formatCurrency(e.montant, e.currency || 'EUR')}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          /* ⚠️ `formaterHorodatage`, PAS `formaterDateSaisie` :
                             ce sont des INSTANTS, convertis dans le fuseau du
                             lecteur. Le serveur les sert suffixés `Z`. */
                          `${formaterHorodatage(e.horodatage)}`
                          + (e.reference ? ` · ${e.reference}` : '')
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── LES NOTIFICATIONS ─────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.recentNotifications')}
              </Typography>
              {recent_notifications && recent_notifications.length > 0 ? (
                <List dense disablePadding>
                  {recent_notifications.map((notification) => (
                    <ListItem key={notification.id} disableGutters>
                      <ListItemText
                        primary={translateNotificationTitle(notification)}
                        secondary={formaterHorodatageEnJour(notification.created_at)}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  {t('dashboard.noRecentNotifications')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 